/**
 * Bootstrap Interface Builder with Dark Mode Support
 * Visual interface builder using Bootstrap components and theme system
 */

import React, { useState, useEffect } from 'react';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { useLocation, useSearchParams } from 'react-router';
import { InterfaceProjectPanel } from '../../components/interface-project/InterfaceProjectPanel';
import { ComponentRenderer, ComponentData } from '../../components/interface-builder/ComponentRenderer';

// Mock component library
const componentLibrary = [
  {
    category: 'Layout',
    components: [
      { name: 'Container', icon: '📦', description: 'Responsive container wrapper' },
      { name: 'Row', icon: '➖', description: 'Horizontal row for grid layout' },
      { name: 'Column', icon: '▌', description: 'Grid column element' },
      { name: 'Card', icon: '🃏', description: 'Content card container' }
    ]
  },
  {
    category: 'Form Elements',
    components: [
      { name: 'Input', icon: '📝', description: 'Text input field' },
      { name: 'Button', icon: '🔘', description: 'Interactive button element' },
      { name: 'Select', icon: '📋', description: 'Dropdown selection list' },
      { name: 'Checkbox', icon: '☑️', description: 'Checkbox input control' },
      { name: 'Radio', icon: '🔘', description: 'Radio button selection' },
      { name: 'Textarea', icon: '📄', description: 'Multi-line text input' }
    ]
  },
  {
    category: 'Data Display',
    components: [
      { name: 'Table', icon: '📊', description: 'Data table component' },
      { name: 'List', icon: '📄', description: 'Organized list display' },
      { name: 'Badge', icon: '🏷️', description: 'Status or label badge' },
      { name: 'Progress', icon: '📈', description: 'Progress indicator bar' }
    ]
  },
  {
    category: 'Navigation',
    components: [
      { name: 'Navbar', icon: '🧭', description: 'Navigation bar header' },
      { name: 'Breadcrumb', icon: '🍞', description: 'Navigation breadcrumb' },
      { name: 'Pagination', icon: '⏭️', description: 'Page navigation controls' },
      { name: 'Tabs', icon: '📑', description: 'Tabbed content switcher' }
    ]
  },
  {
    category: 'Chat Components',
    components: [
      { name: 'Chat Input', icon: '💬', description: 'Auto-expanding chat message input field' },
      { name: 'Chat History', icon: '📜', description: 'Scrollable chat message history display' },
      { name: 'LLM Status', icon: '🤖', description: 'AI processing status and metrics indicator' },
      { name: 'Typing Indicator', icon: '⌨️', description: 'Shows when AI is generating response' }
    ]
  },
  {
    category: 'Feedback',
    components: [
      { name: 'Alert', icon: '⚠️', description: 'Notification message' },
      { name: 'Modal', icon: '🖼️', description: 'Overlay dialog window' },
      { name: 'Tooltip', icon: '💬', description: 'Hover information popup' },
      { name: 'Toast', icon: '🍞', description: 'Temporary notification' }
    ]
  }
];

export function BootstrapInterfaceBuilder() {
  const { effectiveTheme } = useBootstrapTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Tab state for Canvas area (builder/preview toggle)
  const [activeTab, setActiveTab] = useState<'builder' | 'preview'>('builder');
  const [canvasComponents, setCanvasComponents] = useState<ComponentData[]>([]);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  
  // Responsive canvas state
  const [screenSize, setScreenSize] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isDraggingComponent, setIsDraggingComponent] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Help popup state
  const [showHelp, setShowHelp] = useState(false);
  
  // Get workflow ID from URL params
  const workflowId = searchParams.get('workflow');
  
  // Sidebar tab state (similar to Workflow Editor)
  const [sidebarActiveTab, setSidebarActiveTab] = useState<'project' | 'components'>(() => {
    const saved = sessionStorage.getItem('interface-sidebar-active-tab');
    return (saved as 'project' | 'components') || 'project';
  });
  
  // Collapsible panel management (similar to ResponsiveWorkflowEditor)
  const [componentsPanelCollapsed, setComponentsPanelCollapsed] = useState(() => {
    const saved = sessionStorage.getItem('interface-components-panel-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [propertiesPanelCollapsed, setPropertiesPanelCollapsed] = useState(() => {
    const saved = sessionStorage.getItem('interface-properties-panel-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Collapsible category states
  const [collapsedCategories, setCollapsedCategories] = useState(() => {
    const saved = sessionStorage.getItem('interface-collapsed-categories');
    return saved ? JSON.parse(saved) : {};
  });

  // Save panel states to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('interface-components-panel-collapsed', JSON.stringify(componentsPanelCollapsed));
  }, [componentsPanelCollapsed]);
  
  useEffect(() => {
    sessionStorage.setItem('interface-properties-panel-collapsed', JSON.stringify(propertiesPanelCollapsed));
  }, [propertiesPanelCollapsed]);
  
  useEffect(() => {
    sessionStorage.setItem('interface-collapsed-categories', JSON.stringify(collapsedCategories));
  }, [collapsedCategories]);
  
  useEffect(() => {
    sessionStorage.setItem('interface-sidebar-active-tab', sidebarActiveTab);
  }, [sidebarActiveTab]);

  // Toggle category collapse state
  const toggleCategory = (categoryName: string) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };


  const handleDragStart = (e: React.DragEvent, component: any) => {
    e.dataTransfer.setData('application/json', JSON.stringify(component));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const componentData = JSON.parse(e.dataTransfer.getData('application/json'));
    const rect = e.currentTarget.getBoundingClientRect();
    const newComponent: ComponentData = {
      id: `${componentData.name.toLowerCase()}-${Date.now()}`,
      name: componentData.name,
      type: componentData.name, // Use name as type for component matching
      icon: componentData.icon,
      x: e.clientX - rect.left - 50, // Offset for better positioning
      y: e.clientY - rect.top - 20,
      properties: getDefaultProperties(componentData.name)
    };
    setCanvasComponents([...canvasComponents, newComponent]);
  };

  // Get canvas dimensions based on screen size
  const getCanvasDimensions = () => {
    switch (screenSize) {
      case 'desktop':
        return { width: 1920, height: 1080, label: 'Desktop Full HD (1920×1080)' };
      case 'tablet':
        return { width: 1024, height: 768, label: 'Tablet Landscape (1024×768)' };
      case 'mobile':
        return { width: 375, height: 812, label: 'Mobile Portrait (375×812)' };
      default:
        return { width: 1920, height: 1080, label: 'Desktop Full HD (1920×1080)' };
    }
  };

  // Get responsive component sizing based on screen type
  const getResponsiveSize = (componentType: string) => {
    const canvasDims = getCanvasDimensions();
    
    // Different scaling strategies for different screen sizes
    let baseSize, maxWidth;
    switch (screenSize) {
      case 'desktop':
        baseSize = 1.0;
        maxWidth = canvasDims.width * 0.3; // 30% of desktop width
        break;
      case 'tablet':
        baseSize = 0.85;
        maxWidth = canvasDims.width * 0.4; // 40% of tablet width
        break;
      case 'mobile':
        baseSize = 0.7;
        maxWidth = canvasDims.width * 0.8; // 80% of mobile width
        break;
      default:
        baseSize = 1.0;
        maxWidth = canvasDims.width * 0.3;
    }

    switch (componentType) {
      case 'Card':
        return { 
          minWidth: Math.min(Math.round(320 * baseSize), maxWidth), 
          minHeight: Math.round(220 * baseSize) 
        };
      case 'Button':
        return { 
          minWidth: Math.round(140 * baseSize), 
          minHeight: Math.round(44 * baseSize) 
        };
      case 'Input':
        return { 
          minWidth: Math.min(Math.round(280 * baseSize), maxWidth), 
          minHeight: Math.round(90 * baseSize) 
        };
      case 'Navbar':
        return { 
          minWidth: screenSize === 'mobile' ? '95%' : '100%', 
          minHeight: Math.round(64 * baseSize) 
        };
      case 'Select':
        return { 
          minWidth: Math.min(Math.round(250 * baseSize), maxWidth), 
          minHeight: Math.round(90 * baseSize) 
        };
      case 'Textarea':
        return { 
          minWidth: Math.min(Math.round(300 * baseSize), maxWidth), 
          minHeight: Math.round(120 * baseSize) 
        };
      default:
        return { 
          minWidth: Math.round(180 * baseSize), 
          minHeight: Math.round(60 * baseSize) 
        };
    }
  };

  // Get default properties for different component types
  const getDefaultProperties = (componentType: string): Record<string, any> => {
    const responsiveSize = getResponsiveSize(componentType);
    switch (componentType) {
      case 'Card':
        return { 
          title: 'New Card', 
          text: 'Card content goes here', 
          buttonText: 'Action',
          ...responsiveSize
        };
      case 'Input':
        return { 
          label: 'Input Label', 
          placeholder: 'Enter text...', 
          type: 'text',
          ...responsiveSize
        };
      case 'Button':
        return { 
          text: 'Click Me', 
          variant: 'btn-primary',
          ...responsiveSize
        };
      case 'Select':
        return { 
          label: 'Select Label', 
          placeholder: 'Choose an option...',
          ...responsiveSize
        };
      case 'Checkbox':
        return { 
          label: 'Check this option',
          ...responsiveSize
        };
      case 'Radio':
        return { 
          label: 'Radio option',
          ...responsiveSize
        };
      case 'Textarea':
        return { 
          label: 'Text Area', 
          placeholder: 'Enter description...', 
          rows: 3,
          ...responsiveSize
        };
      case 'Badge':
        return { 
          text: 'New Badge', 
          variant: 'bg-primary',
          ...responsiveSize
        };
      case 'Progress':
        return { 
          label: 'Progress', 
          value: 75, 
          showValue: true,
          ...responsiveSize
        };
      case 'Navbar':
        return { 
          brand: 'My App',
          ...responsiveSize
        };
      default:
        return responsiveSize;
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Component drag and drop handlers
  const handleComponentMouseDown = (e: React.MouseEvent, componentId: string) => {
    e.stopPropagation();
    const component = canvasComponents.find(c => c.id === componentId);
    if (!component) return;

    setIsDraggingComponent(componentId);
    setSelectedComponentId(componentId);
    
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - component.x,
      y: e.clientY - component.y
    });

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const canvas = document.getElementById('responsive-canvas');
      if (!canvas) return;
      
      const canvasRect = canvas.getBoundingClientRect();
      const canvasDims = getCanvasDimensions();
      
      // Calculate current scale factor - use actual container dimensions
      const canvasContainer = canvas.parentElement;
      const availableWidth = canvasContainer ? canvasContainer.clientWidth - 80 : 800;
      const availableHeight = window.innerHeight - 64 - 200;
      const maxContainerWidth = Math.max(600, availableWidth);
      const maxContainerHeight = Math.max(400, availableHeight);
      const widthBasedScale = (maxContainerWidth - 100) / canvasDims.width;
      const heightBasedScale = (maxContainerHeight - 100) / canvasDims.height;
      const scaleToFit = Math.min(widthBasedScale, heightBasedScale);
      const finalScale = Math.min(Math.max(scaleToFit, 0.1), 0.6) * (zoomLevel / 100);

      const newX = (moveEvent.clientX - canvasRect.left) / finalScale - dragOffset.x + component.x;
      const newY = (moveEvent.clientY - canvasRect.top) / finalScale - dragOffset.y + component.y;

      setCanvasComponents(components =>
        components.map(c =>
          c.id === componentId
            ? { ...c, x: Math.max(0, Math.min(newX, canvasDims.width - 100)), y: Math.max(0, Math.min(newY, canvasDims.height - 50)) }
            : c
        )
      );
    };

    const handleMouseUp = () => {
      setIsDraggingComponent(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Zoom controls
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 25, 25));
  const handleZoomReset = () => setZoomLevel(100);

  // Update component sizes when screen size changes
  React.useEffect(() => {
    if (canvasComponents.length > 0) {
      setCanvasComponents(components => 
        components.map(component => {
          const newSize = getResponsiveSize(component.type);
          return {
            ...component,
            properties: {
              ...component.properties,
              ...newSize
            }
          };
        })
      );
    }
  }, [screenSize]); // Only run when screenSize changes


  const renderBuilderTab = () => (
    <div className="container-fluid h-100 p-0" style={{ overflow: 'hidden' }}>
      <div className="row g-0 h-100">
        {/* Component Library Sidebar */}
        {!componentsPanelCollapsed && (
          <div 
            className="col-auto bg-white border-end h-100 d-flex flex-column"
            style={{
              width: '320px',
              flexShrink: 0,
              maxHeight: '100%'
            }}
          >
          {/* Tab Header with Toggle Button */}
          <div className="border-bottom p-0 d-flex align-items-stretch">
            <ul className="nav nav-tabs border-0 flex-fill" role="tablist">
              <li className="nav-item flex-fill" role="presentation">
                <button
                  className={`nav-link border-0 rounded-0 w-100 d-flex align-items-center justify-content-center gap-1 ${sidebarActiveTab === 'project' ? 'active' : ''}`}
                  onClick={() => setSidebarActiveTab('project')}
                  type="button"
                  role="tab"
                >
                  <span>🎨</span>
                  <span>Project</span>
                </button>
              </li>
              <li className="nav-item flex-fill" role="presentation">
                <button
                  className={`nav-link border-0 rounded-0 w-100 d-flex align-items-center justify-content-center gap-1 ${sidebarActiveTab === 'components' ? 'active' : ''}`}
                  onClick={() => setSidebarActiveTab('components')}
                  type="button"
                  role="tab"
                >
                  <span>🧩</span>
                  <span>Components</span>
                </button>
              </li>
            </ul>
            
            {/* Sidebar Collapse Toggle */}
            <div className="border-start d-flex align-items-center">
              <button
                onClick={() => setComponentsPanelCollapsed(!componentsPanelCollapsed)}
                className={`btn border-0 d-flex align-items-center justify-content-center ${componentsPanelCollapsed ? 'text-primary' : 'text-muted'}`}
                style={{ 
                  width: '40px', 
                  height: '46px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px'
                }}
                title={`${componentsPanelCollapsed ? 'Expand' : 'Collapse'} Component Library`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.1)';
                  e.currentTarget.style.color = '#0d6efd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = componentsPanelCollapsed ? '#0d6efd' : '#6b7280';
                }}
              >
                <span>◀️</span>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-fill overflow-hidden d-flex flex-column">
            {sidebarActiveTab === 'project' && (
              <InterfaceProjectPanel 
                className="h-100 overflow-auto"
                onInterfaceUpdate={(name, description) => {
                  // Handle interface property updates
                }}
              />
            )}
            {sidebarActiveTab === 'components' && (
              <div className="flex-fill overflow-hidden d-flex flex-column p-3">
                {/* Component Categories - Scrollable */}
                <div className="flex-fill overflow-auto">
                  {componentLibrary.map((category) => (
                    <div key={category.category} className="mb-3">
                      {/* Category Header with Toggle */}
                      <div 
                        className="d-flex align-items-center justify-content-between p-2 bg-light rounded cursor-pointer hover-bg"
                        onClick={() => toggleCategory(category.category)}
                        style={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: collapsedCategories[category.category] ? '#f8f9fa' : '#e9ecef'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#dee2e6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = collapsedCategories[category.category] ? '#f8f9fa' : '#e9ecef';
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <span 
                            className="me-2 d-inline-block"
                            style={{ 
                              transform: collapsedCategories[category.category] ? 'rotate(-90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}
                          >
                            ▼
                          </span>
                          <h6 className="fw-semibold mb-0 small">
                            {category.category}
                          </h6>
                          <span className="badge bg-primary ms-2 small">
                            {category.components.length}
                          </span>
                        </div>
                      </div>

                      {/* Category Components */}
                      {!collapsedCategories[category.category] && (
                        <div className="mt-2">
                          {category.components.map((component) => (
                            <div
                              key={component.name}
                              className="card mb-2 cursor-pointer hover-shadow"
                              draggable
                              onDragStart={(e) => handleDragStart(e, component)}
                              style={{ cursor: 'grab' }}
                            >
                              <div className="card-body p-2">
                                <div className="d-flex align-items-center">
                                  <span className="me-2">{component.icon}</span>
                                  <div>
                                    <div className="fw-medium small">{component.name}</div>
                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                      {component.description}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>
        )}

        {/* Collapsed Components Panel Indicator */}
        {componentsPanelCollapsed && (
          <div 
            className="col-auto bg-light border-end h-100 d-flex align-items-center justify-content-center" 
            style={{ 
              width: '60px', 
              flexShrink: 0
            }}
          >
            <button
              onClick={() => setComponentsPanelCollapsed(false)}
              className="btn btn-primary shadow-sm d-flex align-items-center justify-content-center"
              style={{ 
                width: '40px', 
                height: '40px',
                borderRadius: '8px'
              }}
              title="Expand Component Library"
            >
              <span style={{ fontSize: '16px' }}>{sidebarActiveTab === 'project' ? '🎨' : '🧩'}</span>
            </button>
          </div>
        )}

        {/* Canvas Area */}
        <div 
          className="col position-relative overflow-hidden d-flex flex-column" 
          style={{ 
            height: '100%',
            boxSizing: 'border-box'
          }}
        >
        <div className="p-3">
          {/* Navigation and Tabs with Action Buttons */}
          <div className="mb-3">
            {/* Navigation Row */}
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex gap-2">
                {/* Navigation Buttons */}
                <a
                  href="/workflows"
                  className="btn btn-sm btn-outline-secondary bg-white bg-opacity-95 shadow-sm d-flex align-items-center gap-1 text-decoration-none"
                  style={{ backdropFilter: 'blur(10px)', fontSize: '12px' }}
                >
                  <span>←</span>
                  <span>Workflows</span>
                </a>
                
                {workflowId && (
                  <a
                    href={`/workflows/${workflowId}`}
                    className="btn btn-sm btn-outline-primary bg-white bg-opacity-95 shadow-sm d-flex align-items-center gap-1 text-decoration-none"
                    style={{ backdropFilter: 'blur(10px)', fontSize: '12px' }}
                    title="Back to Workflow Editor"
                  >
                    <span>✏️</span>
                    <span>Editor</span>
                  </a>
                )}
              </div>

              {/* Help Button */}
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="btn btn-sm btn-outline-info bg-white bg-opacity-95 shadow-sm d-flex align-items-center justify-content-center"
                style={{ 
                  backdropFilter: 'blur(10px)', 
                  width: '32px', 
                  height: '32px',
                  fontSize: '14px'
                }}
                title="Keyboard Shortcuts"
              >
                ?
              </button>
            </div>

            {/* Tab Row */}
            <div className="d-flex align-items-center justify-content-between">
              <ul className="nav nav-tabs mb-0 flex-grow-1">
                <li className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'builder' ? 'active' : ''}`}
                    onClick={() => setActiveTab('builder')}
                  >
                    🔨 Interface Builder
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'preview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('preview')}
                  >
                    👁️ Preview
                  </button>
                </li>
              </ul>
              
              {/* Action Buttons */}
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-outline-secondary btn-sm"
                  title="Save interface configuration"
                >
                  💾 Save
                </button>
                <button 
                  className="btn btn-outline-primary btn-sm"
                  title="Export interface as standalone app"
                >
                  📤 Export
                </button>
              </div>
            </div>
          </div>

          {/* Help Popup */}
          {showHelp && (
            <div 
              className="position-absolute bg-white bg-opacity-95 shadow-lg border rounded p-3 mb-3"
              style={{ 
                top: '50px', 
                right: '12px', 
                zIndex: 20,
                backdropFilter: 'blur(10px)',
                minWidth: '280px'
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 fw-semibold">Interface Builder</h6>
                <button 
                  onClick={() => setShowHelp(false)}
                  className="btn-close btn-close-sm"
                />
              </div>
              <div className="small text-muted d-flex flex-column gap-1">
                <div><strong>Navigation:</strong></div>
                <div>← Workflows - Return to workflow list</div>
                <div>✏️ Editor - Back to workflow editor</div>
                <div className="mt-2"><strong>Actions:</strong></div>
                <div>💾 Save - Save interface configuration</div>
                <div>📤 Export - Export as standalone app</div>
                <div className="mt-2"><strong>Builder:</strong></div>
                <div>Drag components from sidebar to canvas</div>
                <div>Use ↶ Undo / ↷ Redo / 🗑️ Clear tools</div>
              </div>
            </div>
          )}
          
          {/* Canvas Tools */}
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <h6 className="fw-bold mb-0">
                {activeTab === 'builder' ? 'Design Canvas' : 'Interface Preview'}
              </h6>
              {activeTab === 'builder' && (
                <span className="text-muted small">
                  {getCanvasDimensions().label}
                </span>
              )}
            </div>
            
            {activeTab === 'builder' && (
              <div className="d-flex gap-2 flex-wrap">
                {/* Screen Size Switcher */}
                <div className="btn-group btn-group-sm" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name="screenSize"
                    id="desktop-size"
                    checked={screenSize === 'desktop'}
                    onChange={() => setScreenSize('desktop')}
                  />
                  <label className="btn btn-outline-primary" htmlFor="desktop-size" title="Desktop Full HD (1920×1080)">
                    🖥️ Desktop
                  </label>
                  
                  <input
                    type="radio"
                    className="btn-check"
                    name="screenSize"
                    id="tablet-size"
                    checked={screenSize === 'tablet'}
                    onChange={() => setScreenSize('tablet')}
                  />
                  <label className="btn btn-outline-primary" htmlFor="tablet-size" title="Tablet Landscape (1024×768)">
                    📱 Tablet
                  </label>
                  
                  <input
                    type="radio"
                    className="btn-check"
                    name="screenSize"
                    id="mobile-size"
                    checked={screenSize === 'mobile'}
                    onChange={() => setScreenSize('mobile')}
                  />
                  <label className="btn btn-outline-primary" htmlFor="mobile-size" title="Mobile Portrait (375×812)">
                    📱 Mobile
                  </label>
                </div>

                {/* Zoom Controls */}
                <div className="btn-group btn-group-sm" role="group">
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 25}
                    title="Zoom Out"
                  >
                    🔍−
                  </button>
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={handleZoomReset}
                    title="Reset Zoom"
                  >
                    {zoomLevel}%
                  </button>
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 200}
                    title="Zoom In"
                  >
                    🔍+
                  </button>
                </div>

                {/* Canvas Tools */}
                <div className="btn-group btn-group-sm" role="group">
                  <button className="btn btn-outline-secondary">↶ Undo</button>
                  <button className="btn btn-outline-secondary">↷ Redo</button>
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      if (confirm('Clear all components from canvas?')) {
                        setCanvasComponents([]);
                        setSelectedComponentId(null);
                      }
                    }}
                  >
                    🗑️ Clear
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Canvas Content - Toggle between Builder and Preview */}
          {activeTab === 'builder' ? (
            <div 
              className="p-2 overflow-auto" 
              style={{ 
                minHeight: '600px', 
                maxHeight: '90vh',
                width: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start'
              }}
            >
              <div 
                id="responsive-canvas"
                className={`border rounded position-relative ${effectiveTheme === 'dark' ? 'bg-white border-secondary' : 'bg-white'} shadow-lg`}
                style={(() => {
                  const canvasDims = getCanvasDimensions();
                  
                  // Calculate scale based on available canvas container space
                  const canvasContainer = document.getElementById('responsive-canvas')?.parentElement;
                  const availableWidth = canvasContainer ? canvasContainer.clientWidth - 80 : 800; // Account for padding
                  const availableHeight = window.innerHeight - 64 - 200; // Account for navbar and navigation/toolbars
                  
                  // Always respect container boundaries to prevent overflow
                  const maxContainerWidth = Math.max(600, availableWidth); // Higher minimum
                  const maxContainerHeight = Math.max(400, availableHeight);
                  
                  let scaleToFit;
                  
                  // Simplified scale calculation - safe approach
                  const widthBasedScale = (maxContainerWidth - 100) / canvasDims.width;
                  const heightBasedScale = (maxContainerHeight - 100) / canvasDims.height;
                  
                  // Always choose the smaller scale to maintain aspect ratio
                  scaleToFit = Math.min(widthBasedScale, heightBasedScale);
                  
                  // Safe bounds for all screen types
                  scaleToFit = Math.max(scaleToFit, 0.1); // At least 10%
                  scaleToFit = Math.min(scaleToFit, 0.6); // Max 60% for safety
                  
                  // Apply zoom
                  const finalScale = scaleToFit * (zoomLevel / 100);
                  
                  // Grid size scales with zoom for better visual feedback
                  const gridSize = Math.max(10, 20 * finalScale);
                  
                  return {
                    width: `${canvasDims.width * finalScale}px`, // Apply scale to width directly
                    height: `${canvasDims.height * finalScale}px`, // Apply scale to height directly
                    // Remove transform to avoid positioning issues
                    backgroundImage: `radial-gradient(circle, #e0e0e0 1px, transparent 1px)`,
                    backgroundSize: `${gridSize}px ${gridSize}px`,
                    backgroundPosition: '0 0',
                    margin: '20px auto', // Center with auto margins
                    overflow: 'hidden',
                    border: '2px solid #dee2e6',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    flexShrink: 0, // Prevent flex shrinking
                    maxWidth: '100%' // Ensure it doesn't overflow
                  };
                })()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={(e) => {
                  // Click on empty canvas area deselects components
                  if (e.target === e.currentTarget) {
                    setSelectedComponentId(null);
                  }
                }}
              >
                {/* Canvas Info Overlay */}
                <div className="position-absolute top-0 end-0 d-flex flex-column gap-1" style={{ zIndex: 1000 }}>
                  <div className="bg-primary text-white px-2 py-1 small text-end" style={{ fontSize: '10px' }}>
                    {getCanvasDimensions().label}
                  </div>
                  <div className="bg-info text-white px-2 py-1 small text-end" style={{ fontSize: '10px' }}>
                    Ratio: {(() => {
                      const dims = getCanvasDimensions();
                      const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
                      const divisor = gcd(dims.width, dims.height);
                      return `${dims.width / divisor}:${dims.height / divisor}`;
                    })()}
                  </div>
                  <div className="bg-warning text-dark px-2 py-1 small text-end" style={{ fontSize: '10px' }}>
                    Scale: {(() => {
                      const canvasContainer = document.getElementById('responsive-canvas')?.parentElement;
                      const availableWidth = canvasContainer ? canvasContainer.clientWidth - 80 : 800;
                      const availableHeight = window.innerHeight - 64 - 200;
                      const dims = getCanvasDimensions();
                      const widthScale = (Math.max(600, availableWidth) - 100) / dims.width;
                      const heightScale = (Math.max(400, availableHeight) - 100) / dims.height;
                      const actualScale = Math.min(widthScale, heightScale);
                      return `${(actualScale * zoomLevel).toFixed(1)}% (${availableWidth}px)`;
                    })()}
                  </div>
                  <div className="bg-secondary text-white px-2 py-1 small text-end" style={{ fontSize: '10px' }}>
                    Zoom: {zoomLevel}%
                  </div>
                  <div className="bg-success text-white px-2 py-1 small text-end" style={{ fontSize: '10px' }}>
                    Components: {canvasComponents.length}
                  </div>
                </div>

                {canvasComponents.length === 0 ? (
                  <div className="position-absolute top-50 start-50 translate-middle text-center text-muted">
                    <div className="mb-3" style={{ fontSize: '3rem' }}>🎨</div>
                    <h6>Drop components here</h6>
                    <p className="small">Drag components from the library to start building</p>
                    <div className="small text-muted mt-2">
                      Current viewport: {getCanvasDimensions().label}
                    </div>
                  </div>
                ) : (
                  canvasComponents.map((component) => (
                    <div
                      key={component.id}
                      className={`position-absolute ${selectedComponentId === component.id ? 'border-primary border-2' : 'border border-transparent'} ${isDraggingComponent === component.id ? 'shadow-lg' : ''}`}
                      style={{
                        left: `${component.x}px`, // Use actual position
                        top: `${component.y}px`,  // Use actual position
                        cursor: isDraggingComponent === component.id ? 'grabbing' : 'grab',
                        zIndex: selectedComponentId === component.id ? 10 : 1,
                        backgroundColor: selectedComponentId === component.id ? 'rgba(13, 110, 253, 0.05)' : 'transparent',
                        borderRadius: '4px',
                        transition: isDraggingComponent === component.id ? 'none' : 'all 0.2s ease',
                        transform: `scale(${finalScale})`, // Scale the component itself
                        transformOrigin: 'top left'
                      }}
                      onMouseDown={(e) => handleComponentMouseDown(e, component.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedComponentId(component.id);
                      }}
                    >
                      {/* Drag Layer Overlay */}
                      <div 
                        className="position-absolute w-100 h-100" 
                        style={{ 
                          zIndex: 5, 
                          backgroundColor: 'transparent',
                          cursor: 'grab'
                        }}
                        onMouseDown={(e) => handleComponentMouseDown(e, component.id)}
                      />
                      
                      {/* Component Content */}
                      <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
                        <ComponentRenderer
                          component={component}
                          isSelected={selectedComponentId === component.id}
                          onSelect={() => {}} // Handled by parent click
                          onDelete={(id) => setCanvasComponents(components => 
                            components.filter(c => c.id !== id)
                          )}
                          onUpdate={(id, updates) => {
                            setCanvasComponents(components => 
                              components.map(c => c.id === id ? { ...c, ...updates } : c)
                            );
                          }}
                        />
                      </div>

                      {/* Component Selection Overlay */}
                      {selectedComponentId === component.id && (
                        <div 
                          className="position-absolute top-0 end-0 bg-primary text-white px-1 rounded-bottom-start"
                          style={{ 
                            fontSize: '10px', 
                            zIndex: 15,
                            marginTop: '-1px',
                            marginRight: '-1px'
                          }}
                        >
                          <span className="me-1">{component.name}</span>
                          <button 
                            className="btn btn-sm btn-link text-white p-0"
                            style={{ fontSize: '10px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCanvasComponents(components => 
                                components.filter(c => c.id !== component.id)
                              );
                              setSelectedComponentId(null);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            /* Preview Mode */
            <div className="border rounded position-relative bg-white" style={{ minHeight: '500px' }}>
              <div className="position-absolute top-50 start-50 translate-middle text-center">
                <div className="mb-3" style={{ fontSize: '4rem' }}>🖼️</div>
                <h5>Interface Preview</h5>
                <p className="text-muted">
                  Interactive preview of your interface will be displayed here
                </p>
                <button className="btn btn-outline-primary">
                  Generate Preview
                </button>
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Properties Panel */}
        {!propertiesPanelCollapsed && (
          <div 
            className="col-auto bg-white border-start h-100 d-flex flex-column" 
            style={{ 
              width: '320px', 
              flexShrink: 0,
              maxHeight: '100%'
            }}
          >
          {/* Properties Header with Toggle Button */}
          <div className="border-bottom p-0 d-flex align-items-stretch">
            <div className="flex-fill p-3">
              <h6 className="fw-bold mb-0 d-flex align-items-center">
                <span className="me-2">⚙️</span>
                Properties
              </h6>
            </div>
            
            {/* Properties Panel Collapse Toggle */}
            <div className="border-start d-flex align-items-center">
              <button
                onClick={() => setPropertiesPanelCollapsed(!propertiesPanelCollapsed)}
                className={`btn border-0 d-flex align-items-center justify-content-center ${propertiesPanelCollapsed ? 'text-primary' : 'text-muted'}`}
                style={{ 
                  width: '40px', 
                  height: '46px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px'
                }}
                title={`${propertiesPanelCollapsed ? 'Expand' : 'Collapse'} Properties Panel`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.1)';
                  e.currentTarget.style.color = '#0d6efd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = propertiesPanelCollapsed ? '#0d6efd' : '#6b7280';
                }}
              >
                <span>▶️</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-fill overflow-hidden d-flex flex-column p-3">
            {!selectedComponentId ? (
              <div className="text-muted text-center">
                <div className="mb-2">⚙️</div>
                <p className="small">Select a component to edit its properties</p>
              </div>
            ) : (
              <div className="flex-fill overflow-auto">
                {(() => {
                  const selected = canvasComponents.find(c => c.id === selectedComponentId);
                  if (!selected) return null;
                  
                  return (
                    <>
                      <div className="mb-3">
                        <h6 className="fw-medium mb-2">{selected.name}</h6>
                        <small className="text-muted">{selected.type} Component</small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label small fw-medium">Position</label>
                        <div className="row g-2">
                          <div className="col-6">
                            <input 
                              type="number" 
                              className="form-control form-control-sm" 
                              placeholder="X"
                              value={selected.x}
                              onChange={(e) => {
                                const newX = parseInt(e.target.value) || 0;
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId ? { ...c, x: newX } : c)
                                );
                              }}
                            />
                          </div>
                          <div className="col-6">
                            <input 
                              type="number" 
                              className="form-control form-control-sm" 
                              placeholder="Y"
                              value={selected.y}
                              onChange={(e) => {
                                const newY = parseInt(e.target.value) || 0;
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId ? { ...c, y: newY } : c)
                                );
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Component-specific properties */}
                      {selected.type === 'Card' && (
                        <>
                          <div className="mb-3">
                            <label className="form-label small fw-medium">Title</label>
                            <input 
                              type="text" 
                              className="form-control form-control-sm"
                              value={selected.properties?.title || ''}
                              onChange={(e) => {
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId 
                                    ? { ...c, properties: { ...c.properties, title: e.target.value } } 
                                    : c
                                  )
                                );
                              }}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label small fw-medium">Text</label>
                            <textarea 
                              className="form-control form-control-sm"
                              rows={3}
                              value={selected.properties?.text || ''}
                              onChange={(e) => {
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId 
                                    ? { ...c, properties: { ...c.properties, text: e.target.value } } 
                                    : c
                                  )
                                );
                              }}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label small fw-medium">Button Text</label>
                            <input 
                              type="text" 
                              className="form-control form-control-sm"
                              value={selected.properties?.buttonText || ''}
                              onChange={(e) => {
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId 
                                    ? { ...c, properties: { ...c.properties, buttonText: e.target.value } } 
                                    : c
                                  )
                                );
                              }}
                            />
                          </div>
                        </>
                      )}

                      {selected.type === 'Button' && (
                        <>
                          <div className="mb-3">
                            <label className="form-label small fw-medium">Text</label>
                            <input 
                              type="text" 
                              className="form-control form-control-sm"
                              value={selected.properties?.text || ''}
                              onChange={(e) => {
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId 
                                    ? { ...c, properties: { ...c.properties, text: e.target.value } } 
                                    : c
                                  )
                                );
                              }}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label small fw-medium">Style</label>
                            <select 
                              className="form-select form-select-sm"
                              value={selected.properties?.variant || 'btn-primary'}
                              onChange={(e) => {
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId 
                                    ? { ...c, properties: { ...c.properties, variant: e.target.value } } 
                                    : c
                                  )
                                );
                              }}
                            >
                              <option value="btn-primary">Primary</option>
                              <option value="btn-secondary">Secondary</option>
                              <option value="btn-success">Success</option>
                              <option value="btn-danger">Danger</option>
                              <option value="btn-warning">Warning</option>
                              <option value="btn-info">Info</option>
                              <option value="btn-outline-primary">Outline Primary</option>
                              <option value="btn-outline-secondary">Outline Secondary</option>
                            </select>
                          </div>
                        </>
                      )}

                      {selected.type === 'Input' && (
                        <>
                          <div className="mb-3">
                            <label className="form-label small fw-medium">Label</label>
                            <input 
                              type="text" 
                              className="form-control form-control-sm"
                              value={selected.properties?.label || ''}
                              onChange={(e) => {
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId 
                                    ? { ...c, properties: { ...c.properties, label: e.target.value } } 
                                    : c
                                  )
                                );
                              }}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label small fw-medium">Placeholder</label>
                            <input 
                              type="text" 
                              className="form-control form-control-sm"
                              value={selected.properties?.placeholder || ''}
                              onChange={(e) => {
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId 
                                    ? { ...c, properties: { ...c.properties, placeholder: e.target.value } } 
                                    : c
                                  )
                                );
                              }}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label small fw-medium">Type</label>
                            <select 
                              className="form-select form-select-sm"
                              value={selected.properties?.type || 'text'}
                              onChange={(e) => {
                                setCanvasComponents(components => 
                                  components.map(c => c.id === selectedComponentId 
                                    ? { ...c, properties: { ...c.properties, type: e.target.value } } 
                                    : c
                                  )
                                );
                              }}
                            >
                              <option value="text">Text</option>
                              <option value="email">Email</option>
                              <option value="password">Password</option>
                              <option value="number">Number</option>
                              <option value="tel">Phone</option>
                              <option value="url">URL</option>
                            </select>
                          </div>
                        </>
                      )}

                      {/* Add more component-specific properties as needed */}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          </div>
        )}

        {/* Collapsed Properties Panel Indicator */}
        {propertiesPanelCollapsed && (
          <div 
            className="col-auto bg-light border-start h-100 d-flex align-items-center justify-content-center" 
            style={{ 
              width: '60px', 
              flexShrink: 0
            }}
          >
            <button
              onClick={() => setPropertiesPanelCollapsed(false)}
              className="btn btn-primary shadow-sm d-flex align-items-center justify-content-center"
              style={{ 
                width: '40px', 
                height: '40px',
                borderRadius: '8px'
              }}
              title="Expand Properties Panel"
            >
              <span style={{ fontSize: '16px' }}>⚙️</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );


  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.7);
            }
            50% {
              box-shadow: 0 0 0 10px rgba(13, 110, 253, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(13, 110, 253, 0);
            }
          }
          
          .hover-shadow:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
          }
        `}
      </style>
      {/* Interface Builder - Perfekte Integration ins Layout */}
      <div 
        style={{ 
          height: 'calc(100vh - 64px)'
        }}
      >
        {renderBuilderTab()}
      </div>
    </>
  );
}